import { Card, Grid, pn, Typography, useElementBreakpoints, ValidationErrors } from "@compass/core";
import { FilePanel, Input, Select } from "components";
import {
  DataCenterFile,
  DataCenterFileEditResource,
  DataCenterFileTask,
  DataCenterFileTaskSchoolYear,
  LkDataCenterFileTaskContentId,
  UploadFile,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";

export const DataCenterFilePageBody = (props: {
  dataCenterFile: DataCenterFile;
  setDataCenterFile: (value: DataCenterFile) => void;
  editResource: DataCenterFileEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, largeFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const appTenantName = useSelector((state: RootState) => state.app.tenantName);

  const showContentUpdates =
    props.dataCenterFile.creatingInfoTask?.contents &&
    props.dataCenterFile.creatingInfoTask?.contents.filter(
      (x) => x.id === Number(LkDataCenterFileTaskContentId.Updates)
    ).length > 0
      ? true
      : false;

  return (
    <Card>
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={largeFieldBreakpoints}>
          <FilePanel
            fileName={props.dataCenterFile.uploadFile?.fileName}
            fileRef={props.dataCenterFile.uploadFile?.fileRef}
            fileSize={props.dataCenterFile.uploadFile?.fileSize}
            uploadingData={props.dataCenterFile.uploadFile?.uploadingData}
            imagePreviewData={props.dataCenterFile.uploadFile?.imagePreviewData}
            onFileChange={(fileName, fileSize, uploadingData) => {
              props.setDataCenterFile(
                produce(props.dataCenterFile, (draft) => {
                  draft.uploadFile = {
                    fileName: fileName,
                    fileSize: fileSize,
                    uploadingData: uploadingData,
                    touched: true,
                  } as UploadFile;
                })
              );
            }}
            errors={props.validationErrors[pn<DataCenterFile>("uploadFile")]?.[pn<UploadFile>("fileName")]}
          />
        </Grid>

        <Grid item xs={largeFieldBreakpoints}>
          <Typography variant="caption">
            Be sure that your file is named correctly. File name must begin with the program name and an underscore.
            Your program name is: {appTenantName} For example, {appTenantName}_2018Enrollment.
          </Typography>
        </Grid>

        <Grid item xs={fullFieldBreakpoints}>
          <Select
            label="Data Types"
            multiple
            required
            value={props.editResource.dataTypes?.filter((d) =>
              props.dataCenterFile.creatingInfoTask?.dataTypes?.map((md) => md.id).includes(d.id)
            )}
            options={props.editResource.dataTypes}
            getOptionLabel={(option) => option.name}
            onChange={(e, values) =>
              props.setDataCenterFile(
                produce(props.dataCenterFile, (draft) => {
                  draft.creatingInfoTask!.dataTypes = values;
                })
              )
            }
            errors={
              props.validationErrors[pn<DataCenterFile>("creatingInfoTask")]?.[pn<DataCenterFileTask>("dataTypes")]
            }
          />
        </Grid>

        <Grid item xs={fullFieldBreakpoints}>
          <Select
            label="Contents"
            multiple
            required
            value={props.editResource.contents?.filter((d) =>
              props.dataCenterFile.creatingInfoTask?.contents?.map((md) => md.id).includes(d.id)
            )}
            options={props.editResource.contents}
            getOptionLabel={(option) => option.name}
            onChange={(e, values) =>
              props.setDataCenterFile(
                produce(props.dataCenterFile, (draft) => {
                  draft.creatingInfoTask!.contents = values;
                })
              )
            }
            errors={
              props.validationErrors[pn<DataCenterFile>("creatingInfoTask")]?.[pn<DataCenterFileTask>("contents")]
            }
          />
        </Grid>

        {showContentUpdates && (
          <Grid item xs={fullFieldBreakpoints}>
            <Select
              label="Content Updates"
              multiple
              required
              value={props.editResource.contentUpdates?.filter((d) =>
                props.dataCenterFile.creatingInfoTask?.contentUpdates?.map((md) => md.id).includes(d.id)
              )}
              options={props.editResource.contentUpdates}
              getOptionLabel={(option) => option.name}
              onChange={(e, values) =>
                props.setDataCenterFile(
                  produce(props.dataCenterFile, (draft) => {
                    draft.creatingInfoTask!.contentUpdates = values;
                  })
                )
              }
              errors={
                props.validationErrors[pn<DataCenterFile>("creatingInfoTask")]?.[
                  pn<DataCenterFileTask>("contentUpdates")
                ]
              }
            />
          </Grid>
        )}

        <Grid item xs={fullFieldBreakpoints}>
          <Select
            label="School Years"
            multiple
            required
            value={props.editResource.schoolYears?.filter((x) =>
              props.dataCenterFile.creatingInfoTask?.schoolYears?.map((mx) => mx.schoolYear).includes(x.id)
            )}
            options={props.editResource.schoolYears}
            getOptionLabel={(option) => option.name}
            onChange={(e, values) =>
              props.setDataCenterFile(
                produce(props.dataCenterFile, (draft) => {
                  draft.creatingInfoTask!.schoolYears = values.map((x) => {
                    return { schoolYear: x.id } as DataCenterFileTaskSchoolYear;
                  });
                })
              )
            }
            errors={
              props.validationErrors[pn<DataCenterFile>("creatingInfoTask")]?.[pn<DataCenterFileTask>("schoolYears")]
            }
          />
        </Grid>

        <Grid item xs={fullFieldBreakpoints}>
          <Input
            label="Note"
            value={props.dataCenterFile.note}
            multiline
            onChange={(e) =>
              props.setDataCenterFile(
                produce(props.dataCenterFile, (draft) => {
                  draft.note = e.target.value;
                })
              )
            }
            errors={props.validationErrors[pn<DataCenterFile>("note")]}
          />
        </Grid>
      </Grid>
    </Card>
  );
};
